import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { get } from 'lodash';
import { NakedDialog } from 'MoshtixShared/component-dialog/src/naked-dialog';
import { Div, H3, P } from 'MoshtixShared/component-element';
import tokenStore from 'MoshtixShared/helper-token-store';
import { Button } from 'MoshtixShared/component-button';
import { Icon } from 'MoshtixShared/component-icon';
import logger from 'MoshtixShared/helper-logger';

import { TicketInterface } from 'My/types/app';

import { useMutation } from '../../../hooks/helper-apollo-hooks';
import { REVOKE_INVITATION_MUTATION } from './data-definition';
import { dialogStyles, buttonsWrapperCss, noteCss, noteCssIcon } from './styles';

export type RevokeAssignTicketDialogProps = {
  onClose: (success?: boolean) => void;
  ticket: TicketInterface;
};

export const RevokeAssignTicketDialog: FC<RevokeAssignTicketDialogProps> = ({ onClose: closeDialog, ticket }) => {
  const [revokedTicketSharing, { data, error, loading }] = useMutation(REVOKE_INVITATION_MUTATION, {
    variables: {
      token: tokenStore.getTicketDistroToken(),
    },
  });
  const wasRevoked = get(data, 'viewer.wasRevoked');

  const handleRevoke = useCallback(async () => {
    if (!revokedTicketSharing) {
      return;
    }

    try {
      await logger.logInfo({
        message: `revoking ticket ${ticket.id}`,
      });

      await revokedTicketSharing({
        variables: {
          ticketId: ticket.id,
        },
      });
    } catch (err) {
      logger.logWarning({
        message: `There was a problem when trying to revoke ticket assign: ${JSON.stringify(err)} `,
      });
    }
  }, [revokedTicketSharing, ticket.id]);

  useEffect(() => {
    if (wasRevoked === null || wasRevoked === undefined) {
      return;
    }

    logger.logInfo({
      message: `Ticket invitation was ${wasRevoked ? 'successfully revoked' : 'was not revoked'}`,
    });

    if (wasRevoked) {
      closeDialog(true);
    }
  }, [wasRevoked, closeDialog]);

  const errorMessage = useMemo(() => {
    // show the error message in case theres an error, or the revoke just fails
    if (error || wasRevoked === false) {
      return (
        <Div css={noteCss}>
          <Icon css={noteCssIcon} type="exclamation" />
          <Div>
            <P>There was an error when trying to revoke the ticket!</P>
            <P>Please contact the customer support</P>
          </Div>
        </Div>
      );
    }

    return null;
  }, [error, wasRevoked]);

  return (
    <>
      <NakedDialog show onCloseClick={() => closeDialog()}>
        <Div css={dialogStyles}>
          <H3>You are revoking ticket access</H3>
          {errorMessage}
          <P>The assigned attendee will be notified and no longer have access to this ticket for the event.</P>
          <Div css={buttonsWrapperCss}>
            <Button disabled={loading} buttonType="tertiaryAlternate" onClick={() => closeDialog()}>
              cancel
            </Button>
            <Button disabled={loading} loading={loading} onClick={handleRevoke}>
              revoke ticket
            </Button>
          </Div>
        </Div>
      </NakedDialog>
    </>
  );
};
